import React, { createContext, createRef, useEffect, useMemo, useState, memo, useCallback } from "react";
import { useDispatch, connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { GlobalStyles, Page, PageContent, Title, Actions, Subtitle } from "./styles";
import Grid from "../../Grid";
import Loader from "../../Loader";
import Whatsapp from "../../Whatsapp";
import DesktopMenu from "../components/DesktopMenu";
import MobileHeader from "../components/MobileHeader";
//import Tabs from "../components/Tabs";
import ImportMembersModal from "../components/ModalImportMembers";
import constants from "../../../constants";
import { setCurrentPage } from "../../../store/actions/page";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { Center } from "../../StyledComponents";
import { useLayout } from "../../../providers/LayoutProvider";
import { User } from "../../../types";
import { showModal } from "../../../store/actions/modal";
import GoBack from "../../GoBack";
import PhoneNumberModalContext from '../components/ModalPhoneNumberContext';
import OnboardingModalsProvider from '../../../providers/OnboardingContext';
import MoraLevelOne from '../../../pages/Dashboard/components/MoraLevelOne';
import MoraLevelTwo from '../../../pages/Dashboard/components/MoraLevelTwo';
import useUnreadCountPolling from '../../NotificationPanel/hooks/useUnreadCountPolling';



interface ComponentProps {
  children;
  userInformation: User;
  expertInformation;
  community;
  communityStates: {
    loading: boolean;
    success: boolean;
    error: string | boolean;
  };
  expertStates: {
    loading: boolean;
    success: boolean;
    error: string | boolean;
  };
  paddings?: boolean;
  navigationLevel?: number;
}

const DashboardLayoutComponent = ({
  children,
  userInformation,
  expertInformation,
  expertStates,
  community,
  communityStates,
  paddings = true,
  navigationLevel: propNavigationLevel,
}: ComponentProps) => {
  const navigate = useNavigate();
  const { layout } = useLayout();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isMobile } = useWindowSize();

  useUnreadCountPolling(!!userInformation);

  // Auto-detect navigationLevel based on route depth
  // Level 1: /dashboard/community, /dashboard/membership, etc.
  // Level 2: /dashboard/experience/:idGroup/..., /dashboard/membership/:idGroup/..., etc.
  const autoNavigationLevel = useMemo(() => {
    if (propNavigationLevel !== undefined) {
      return propNavigationLevel;
    }

    const pathname = location.pathname;
    // Count segments after /dashboard
    const segments = pathname.split('/').filter(Boolean);
    const dashboardIndex = segments.indexOf('dashboard');

    if (dashboardIndex === -1) return 1;

    // If there's an ID-like segment (UUID or number) after a section, it's level 2
    // Pattern: /dashboard/section/:id/...
    const sectionIndex = dashboardIndex + 1;
    if (segments.length > sectionIndex + 1) {
      const potentialId = segments[sectionIndex + 1];
      // Check if it looks like an ID (UUID or long number)
      const isId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(potentialId) ||
        /^\d+$/.test(potentialId);

      if (isId) {
        return 2;
      }
    }

    return 1;
  }, [location.pathname, propNavigationLevel]);

  const navigationLevel = autoNavigationLevel;

  const pageContentRef = createRef<HTMLElement>();
  const footerRef = createRef<HTMLElement>();
  const headerRef = createRef<HTMLElement>();
  const [minHeight, setMinHeight] = useState(0);
  const [currentPageLocally, setCurrentPageLocally] = useState<{
    name: string;
    regex: RegExp;
  }>();
  const [publishingPage, setPublishingPage] = useState<boolean>(false);
  // const [phoneNumberModalVisible, setPhoneNumberModalVisible] = useState(false);

  // Memoize current page detection to avoid recalculating on every render
  const currentPage = useMemo(() => {
    if (!location) return undefined;
    return constants.pages.find((page) => location.pathname.match(page.regex));
  }, [location.pathname]);

  useEffect(() => {
    if (currentPage) {
      setCurrentPageLocally(currentPage);
      dispatch(setCurrentPage(currentPage));
    }

    if (pageContentRef?.current) {
      pageContentRef.current.scrollTo(0, 0);
    }

    document.body.style.height = "auto";
    document.body.style.overflow = "auto";
  }, [currentPage, dispatch]);

  // Calculate minHeight when refs are available
  // Using a callback to avoid dependency issues with refs
  useEffect(() => {
    const updateMinHeight = () => {
      if (headerRef.current && footerRef.current) {
        setMinHeight(
          headerRef.current.scrollHeight + footerRef.current.scrollHeight
        );
      }
    };
    
    updateMinHeight();
    // Recalculate on window resize
    window.addEventListener('resize', updateMinHeight);
    return () => window.removeEventListener('resize', updateMinHeight);
  }, []);

  const publishingPageMemo = useMemo(() => {
    return currentPageLocally?.name === "NEW_EXPERIENCE" ||
      currentPageLocally?.name === "NEW_MEMBERSHIP" ||
      currentPageLocally?.name === "NEW_COMMUNITY_EVENT";
  }, [currentPageLocally?.name]);

  useEffect(() => {
    setPublishingPage(publishingPageMemo);
  }, [publishingPageMemo]);

  useEffect(() => {
    if (
      !expertInformation &&
      expertStates.success &&
      !!currentPageLocally &&
      currentPageLocally?.name !== "MY_PROFILE" &&
      currentPageLocally?.name !== "SUBSCRIPTIONS_LIST" &&
      currentPageLocally?.name !== "MANAGE_SUBSCRIPTIONS" &&
      currentPageLocally?.name !== "UPDATE_PAYMENT_METHOD" &&
      currentPageLocally?.name !== "LINKED_GATEWAYS" &&
      currentPageLocally?.name !== "COMPLETE_PROFILE"
    ) {
      navigate("/onboarding/complete-profile");
    }
  }, [expertInformation, expertStates.success, currentPageLocally?.name, navigate]);

  const isMoraLevelOne = useMemo(() => {
    return expertInformation?.delinquency === "Level_1";
  }, [expertInformation?.delinquency]);

  // Memoize layout-derived values to prevent unnecessary re-renders
  const headerVisible = useMemo(() => layout?.header?.visible, [layout?.header?.visible]);
  const pageTitle = useMemo(() => layout?.page?.title, [layout?.page?.title]);
  const pageSubtitle = useMemo(() => layout?.page?.subtitle, [layout?.page?.subtitle]);
  const pageBack = useMemo(() => layout?.page?.back, [layout?.page?.back]);
  const headerActions = useMemo(() => layout?.header?.actions, [layout?.header?.actions]);
  const dashboardTabsVisible = useMemo(() => layout?.dashboard?.tabs?.visible, [layout?.dashboard?.tabs?.visible]);
  const dashboardTabsActiveTab = useMemo(() => layout?.dashboard?.tabs?.activeTab, [layout?.dashboard?.tabs?.activeTab]);
  const dashboardTabsComponent = useMemo(() => layout?.dashboard?.tabs?.component, [layout?.dashboard?.tabs?.component]);
  const showWhatsapp = useMemo(() => layout?.show_whatsapp, [layout?.show_whatsapp]);

  // Memoize MoraLevelOne style to avoid creating new object on every render
  const moraLevelOneStyle = useMemo(() => ({
    margin: isMobile ? "0px" : "-42px -68px 26px"
  }), [isMobile]);

  // Memoize MobileHeader and DesktopMenu to prevent unnecessary re-renders
  const memoizedMobileHeader = useMemo(() => (
    <>
      <div style={{ height: "25px" }} />
      <MobileHeader navigationLevel={navigationLevel} />
      <div style={{ height: "32px" }} />
    </>
  ), [navigationLevel]);

  const memoizedDesktopMenu = useMemo(() => (
    <DesktopMenu isMoraLevelOne={isMoraLevelOne} />
  ), [isMoraLevelOne]);

  // Memoize header content to avoid re-rendering on every layout change
  const headerContent = useMemo(() => {
    if (!headerVisible) return null;
    
    return (
      <>
        <Center horizontal="space" vertical="center">
          {pageTitle && <Title>{pageTitle}</Title>}
          {headerActions && <Actions>{headerActions}</Actions>}
        </Center>
        {!pageSubtitle ? <div style={{ height: 32 }} /> : <div style={{ height: 8 }} />}
      </>
    );
  }, [headerVisible, pageTitle, headerActions, pageSubtitle]);

  // Memoize mobile actions
  const mobileActions = useMemo(() => {
    if (!isMobile || !headerActions || headerActions.length === 0 || navigationLevel !== 1) {
      return null;
    }
    
    return (
      <>
        <Grid.Container>
          <Grid.Row>
            <Grid.Col>
              <Actions>{headerActions}</Actions>
            </Grid.Col>
          </Grid.Row>
        </Grid.Container>
        <div style={{ height: "32px" }} />
      </>
    );
  }, [isMobile, headerActions, navigationLevel]);

  // Memoize dashboard tabs
  const dashboardTabs = useMemo(() => {
    if (!dashboardTabsVisible || !dashboardTabsActiveTab) return null;
    
    return (
      <>
        {dashboardTabsComponent?.map((comp, idx) =>
          React.cloneElement(comp, { key: `dashboard-tab-${idx}` })
        )}
        <div style={{ height: "32px" }} />
      </>
    );
  }, [dashboardTabsVisible, dashboardTabsActiveTab, dashboardTabsComponent]);

  // useEffect(() => {
  //   if (
  //     !!userInformation &&
  //     (!userInformation?.expert ||
  //       (!!userInformation?.expert &&
  //         !!userInformation?.expert?.billing_info?.country)) &&
  //     !userInformation?.phone_number
  //   ) {
  //     dispatch(showModal("modal-phone-number"));
  //     setPhoneNumberModalVisible(true)
  //   } else {
  //     setPhoneNumberModalVisible(false)
  //   }
  // }, [userInformation]);

  if (expertStates.loading) {
    return <Loader />;
  }

  return (
    <>
      {/* <PhoneNumberModalContext.Provider value={phoneNumberModalVisible}> */}
      <OnboardingModalsProvider>
        <MoraLevelOne style={moraLevelOneStyle} />

        <GlobalStyles />

        <Page id="dashboard-layout" isMoraLevelOne={isMoraLevelOne}>
          {isMobile ? memoizedMobileHeader : memoizedDesktopMenu}

          <PageContent ref={pageContentRef} id="dashboard-page-content" isMoraLevelOne={isMoraLevelOne}>
            <>
              {!isMobile && (
                <>
                  {pageBack && <GoBack backTo={pageBack} showAlt />}
                  {headerContent}
                  {pageSubtitle && (
                    <>
                      <Subtitle>{pageSubtitle}</Subtitle>
                      <div style={{ height: 32 }} />
                    </>
                  )}
                </>
              )}

              {mobileActions}

              {dashboardTabs}
            </>

            {React.createElement(children.type, {
              ...{
                ...children.props,
                pageContentRef,
              },
            })}
          </PageContent>
        </Page>

        <ImportMembersModal />
        {showWhatsapp && <Whatsapp floating options={{ marginBottom: 40 }} />}
        {/* </PhoneNumberModalContext.Provider> */}
      </OnboardingModalsProvider>

      <MoraLevelTwo />
    </>
  );
};

const state = ({ userStore, communityStore }) => {
  const { data: user, states: expertStates } = userStore.information;
  const { data: community, states: communityStates } = communityStore.community;

  return {
    community,
    communityStates,
    userInformation: user,
    expertInformation: user?.expert,
    expertStates,
  };
};

// Memoize the component to prevent unnecessary re-renders
// Only re-render if props actually change
const MemoizedDashboardLayout = memo(DashboardLayoutComponent, (prevProps, nextProps) => {
  // Custom comparison function
  return (
    prevProps.userInformation === nextProps.userInformation &&
    prevProps.expertInformation === nextProps.expertInformation &&
    prevProps.expertStates.loading === nextProps.expertStates.loading &&
    prevProps.expertStates.success === nextProps.expertStates.success &&
    prevProps.expertStates.error === nextProps.expertStates.error &&
    prevProps.community === nextProps.community &&
    prevProps.communityStates.loading === nextProps.communityStates.loading &&
    prevProps.communityStates.success === nextProps.communityStates.success &&
    prevProps.communityStates.error === nextProps.communityStates.error &&
    prevProps.navigationLevel === nextProps.navigationLevel &&
    prevProps.children === nextProps.children
  );
});

export default connect(state)(MemoizedDashboardLayout);
